import React, { useState, useEffect } from "react";
import SideBar from "../inc/SideBar";
import { Container, Dropdown, Image } from "react-bootstrap";
import { FiMoreVertical, FiMenu, FiPlus, FiArrowUp, FiArrowDown, FiArrowLeft } from "react-icons/fi";
import { FaRegEye, FaRegEdit, FaRegTrashAlt } from "react-icons/fa";
import { IoIosCloseCircleOutline } from "react-icons/io";
import SearchIcon from "../../web_components/images/search.png";
import FilterIcon from "../images/filter.svg";
import ExportIcon from "../images/exporticon.svg";
import AxiosConfig from "../services/AxiosConfig";
import "./Invoices.scss";
import NotificationIcon from "../images/notification.svg";
import { Link } from "react-router-dom";
import { Badge, Button, Form } from "react-bootstrap";
import { TbTruckDelivery } from "react-icons/tb";
import { LuLayers } from "react-icons/lu";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { toDisplayDate, toInputDate, todayDisplayDate, toComparableDate } from "../services/dateFormat";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import InvoicesFilterSidebar from "../inc/InvoicesFilterSidebar";

const Invoices = () => {
  const userDetail = JSON.parse(localStorage.getItem("userDetail") || "{}");
  const userRole = userDetail.profile;
  const isRoleStaff = userRole === "Staff" || userRole === "Admin";
  const isRoleClient = userRole === "Client" || userRole === "User" || userRole === "Shipper";
  const access = localStorage.getItem("access") || "";
  const isAdminOrStaff =
    access.includes("adminAccess") ||
    access.includes("staffAccess") ||
    isRoleStaff;
  const canManageInvoices = isAdminOrStaff;
  const partnerIdLabel = isRoleClient ? "Carrier ID" : "Customer ID";
  const getPartnerIdValue = (invoice) => {
    if (!invoice) return "N/A";
    if (isRoleClient) {
      return invoice.carrierId || invoice.carrier || "N/A";
    }
    return invoice.customerId || "N/A";
  };

  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({
    totalInvoice: 0,
    paid: 0,
    pending: 0,
    overdue: 0,
    invoices: 0
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [isBodyClassActive, setIsBodyClassActive] = useState(false);
  const [exportMode, setExportMode] = useState(false);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [view, setView] = useState("list");
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [currentViewInvoice, setCurrentViewInvoice] = useState(null);
  const [formData, setFormData] = useState({
    shipment: "",
    customerId: "",
    carrierId: "",
    invoiceNumber: "",
    date: toInputDate(new Date()),
    fromDate: "",
    status: "Pending",
    toDate: "",
    amount: "",
    operationalDetail: { vehicle: "-", quantity: "-", loading: "-", unloading: "-" },
    paymentBreakdown: []
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const normalizeDateForInput = (rawDate) => toInputDate(rawDate);

  const isMissingQuantity = (qty) =>
    !qty || qty === "N/A" || qty === "-" || String(qty).trim() === "";

  /** Align with Orders: General cargo = pallets/weight, Tanker = liters (quantity) */
  const formatMaterialQuantity = (materials, category = "") => {
    const list = Array.isArray(materials) ? materials.filter(Boolean) : [];
    if (!list.length) return "N/A";

    const cat = String(category || list[0]?.category || "").toLowerCase();
    const isTanker = cat.includes("tanker");

    if (isTanker) {
      const liters = list.reduce(
        (sum, m) => sum + (parseFloat(m.quantity ?? m.Quantity) || 0),
        0
      );
      return liters > 0 ? `${liters} L` : "N/A";
    }

    const pallets = list.reduce(
      (sum, m) =>
        sum + (parseFloat(m.numberOfPallets ?? m.NumberOfPallets) || 0),
      0
    );
    const weight = list.reduce(
      (sum, m) =>
        sum +
        (parseFloat(
          m.totalWeightOfPallets ?? m.TotalWeightOfPallets ?? m.totalWeight
        ) || 0),
      0
    );
    const looseQty = list.reduce(
      (sum, m) => sum + (parseFloat(m.quantity ?? m.Quantity) || 0),
      0
    );

    const parts = [];
    if (pallets > 0) parts.push(`${pallets} Pallets`);
    if (weight > 0) parts.push(`${weight} Kg`);
    if (!parts.length && looseQty > 0) {
      parts.push(String(looseQty));
    }

    return parts.length ? parts.join(" / ") : "N/A";
  };

  const SHIPMENT_NOT_FOUND_MSG =
    "Shipment number not found. Please check the shipment number.";

  const fetchQuoteByShipment = async (shipmentRef) => {
    const response = await AxiosConfig.instance.get(
      `/get_quote_by_reference/${encodeURIComponent(shipmentRef)}`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(SHIPMENT_NOT_FOUND_MSG);
    }
    return response.data.data;
  };

  /** Backfill quantity (and carrier) from the linked shipment when missing on the invoice */
  const enrichInvoiceQuantityFromShipment = async (invoice, { silent } = {}) => {
    if (!invoice?.shipment || !isMissingQuantity(invoice.operationalDetail?.quantity)) {
      return invoice;
    }
    try {
      const quote = await fetchQuoteByShipment(String(invoice.shipment).trim());
      const materials = Array.isArray(quote.MaterialInformation)
        ? quote.MaterialInformation
        : [];
      const quantity = formatMaterialQuantity(materials, quote.category);
      if (isMissingQuantity(quantity)) return invoice;

      const approvedCarrier =
        (quote.ChooseCarrier || []).find((c) => c.Status === "Approved") ||
        (quote.ChooseCarrier || [])[0];
      const carrierLabel =
        quote.carrier ||
        approvedCarrier?.Company_name ||
        approvedCarrier?.companyName ||
        approvedCarrier?.carrierName ||
        "";

      return {
        ...invoice,
        carrierId: invoice.carrierId || carrierLabel || invoice.carrierId,
        operationalDetail: {
          ...(invoice.operationalDetail || {
            vehicle: "-",
            quantity: "-",
            loading: "-",
            unloading: "-",
          }),
          quantity,
        },
      };
    } catch (error) {
      if (!silent) {
        console.error("Could not enrich invoice quantity:", error);
      }
      return invoice;
    }
  };

  const handleShipmentLookup = async (shipmentOverride) => {
    const shipmentRef = String(shipmentOverride ?? formData.shipment ?? "").trim();
    if (!shipmentRef) {
      toast.error("Please enter a shipment number.");
      return;
    }

    try {
      const quote = await fetchQuoteByShipment(shipmentRef);

      const loading = quote.LOADING_INFORMATIONS?.[0] || {};
      const delivery = quote.DELIVERY_INFORMATIONS?.[0] || {};
      const materials = Array.isArray(quote.MaterialInformation)
        ? quote.MaterialInformation
        : [];
      const material = materials[0] || {};

      const vehicleType = material.type || material.category || quote.category || "N/A";
      const quantity = formatMaterialQuantity(materials, quote.category);

      const loadingStr = [
        [loading.city || loading.loadingCity, loading.loadingCountry || loading.country]
          .filter(Boolean)
          .join(", "),
        loading.Date || loading.date || "",
      ]
        .filter(Boolean)
        .join("\n") || "N/A";

      const unloadingStr = [
        [
          delivery.delivery_city || delivery.deliveryCity,
          delivery.deliveryCountry || delivery.country,
        ]
          .filter(Boolean)
          .join(", "),
        delivery.delivery_Date || delivery.Date || delivery.date || "",
      ]
        .filter(Boolean)
        .join("\n") || "N/A";

      const approvedCarrier =
        (quote.ChooseCarrier || []).find((c) => c.Status === "Approved") ||
        (quote.ChooseCarrier || [])[0];

      const parseMoney = (value) => {
        if (value == null || value === "") return 0;
        const n = Number(String(value).replace(/[^\d.-]/g, ""));
        return Number.isFinite(n) ? n : 0;
      };

      const carrierPrice = parseMoney(approvedCarrier?.Price);
      const quotePrize = parseMoney(quote.prize);
      const quoteTotal = parseMoney(quote.totalprize);

      // Freight = carrier haul (larger amount). Mottruck = platform fee (smaller).
      let carrierFreight = carrierPrice || quotePrize || quoteTotal;
      const candidates = [carrierPrice, quotePrize, quoteTotal].filter((n) => n > 0);
      let platformFee = 0;
      if (candidates.length >= 2) {
        const largest = Math.max(...candidates);
        const smallest = Math.min(...candidates);
        if (largest > smallest) {
          carrierFreight = largest;
          platformFee = smallest;
        }
      }
      if (!platformFee && carrierFreight > 0) {
        // Same rule as quote approval: 15% platform margin on carrier freight
        platformFee = Math.round(carrierFreight * 0.15 * 100) / 100;
      }

      const vatRate = 0.2;
      const formatMoney = (n) => `${Number(n) || 0}€`;
      const formatVat = (n) => `${(Number(n) || 0) * vatRate}€ | 20%`;
      const formatTotal = (n) => `${(Number(n) || 0) * (1 + vatRate)}€`;

      const customerLabel =
        loading.Company_name ||
        quote.applicant ||
        (quote.client_id ? String(quote.client_id) : "") ||
        (quote.userId ? String(quote.userId) : "");

      const carrierLabel =
        quote.carrier ||
        approvedCarrier?.Company_name ||
        approvedCarrier?.companyName ||
        approvedCarrier?.carrierName ||
        "";

      setFormData((prev) => ({
        ...prev,
        customerId: customerLabel || prev.customerId,
        carrierId: carrierLabel || prev.carrierId,
        fromDate: normalizeDateForInput(loading.Date || loading.date) || prev.fromDate,
        toDate:
          normalizeDateForInput(
            delivery.delivery_Date || delivery.Date || delivery.date
          ) || prev.toDate,
        amount:
          carrierFreight || platformFee
            ? formatTotal(platformFee + carrierFreight)
            : prev.amount,
        invoiceBy: customerLabel || prev.invoiceBy,
        operationalDetail: {
          vehicle: vehicleType,
          quantity,
          loading: loadingStr,
          unloading: unloadingStr,
        },
        // Carrier = freight (usually the larger amount); Mottruck = platform fee
        paymentBreakdown: [
          {
            party: "Mottruck",
            service: "Platform services and coordination",
            amount: formatMoney(platformFee),
            vat: formatVat(platformFee),
            total: formatTotal(platformFee),
          },
          {
            party: "Carrier",
            service: "Freight service",
            amount: formatMoney(carrierFreight),
            vat: formatVat(carrierFreight),
            total: formatTotal(carrierFreight),
          },
        ],
      }));
      toast.success("Shipment details loaded.");
    } catch (error) {
      console.error("Error fetching shipment info:", error);
      // Never surface "Quote not found" - autofill is by shipment number only
      toast.error(SHIPMENT_NOT_FOUND_MSG);
    }
  };

  const resetFormData = () => ({
    shipment: "",
    customerId: "",
    carrierId: "",
    invoiceNumber: "",
    date: toInputDate(new Date()),
    fromDate: "",
    status: "Pending",
    toDate: "",
    amount: "",
    operationalDetail: { vehicle: "-", quantity: "-", loading: "-", unloading: "-" },
    paymentBreakdown: [],
  });

  const populateFormFromInvoice = (invoice) => ({
    shipment: invoice.shipment || "",
    customerId: invoice.customerId || "",
    carrierId: invoice.carrierId || "",
    invoiceNumber: invoice.invoiceNumber || "",
    fromDate: toInputDate(invoice.fromDate),
    status: invoice.status || "Pending",
    toDate: toInputDate(invoice.toDate),
    operationalDetail: invoice.operationalDetail || { vehicle: "-", quantity: "-", loading: "-", unloading: "-" },
    paymentBreakdown: normalizePaymentBreakdown(invoice.paymentBreakdown || []),
    reference: invoice.reference,
    date: toInputDate(invoice.date) || toInputDate(new Date()),
    amount: invoice.amount,
    invoiceBy: invoice.invoiceBy,
  });

  const getOperationalDetail = (invoice) =>
    invoice?.operationalDetail || {
      vehicle: "-",
      quantity: "-",
      loading: "-",
      unloading: "-",
    };

  const parseBreakdownAmount = (raw) => {
    if (raw == null || raw === "") return 0;
    const n = Number(String(raw).replace(/[^\d.-]/g, ""));
    return Number.isFinite(n) ? n : 0;
  };

  /** Fix legacy invoices where Mottruck/Carrier amounts were swapped */
  const normalizePaymentBreakdown = (breakdown) => {
    if (!Array.isArray(breakdown) || breakdown.length < 2) return breakdown || [];
    const mottruckIdx = breakdown.findIndex((i) => /mottruck/i.test(i?.party || ""));
    const carrierIdx = breakdown.findIndex((i) => /carrier/i.test(i?.party || ""));
    if (mottruckIdx < 0 || carrierIdx < 0) return breakdown;

    const mottruck = breakdown[mottruckIdx];
    const carrier = breakdown[carrierIdx];
    const mottruckAmt = parseBreakdownAmount(mottruck.amount);
    const carrierAmt = parseBreakdownAmount(carrier.amount);

    // Business rule: freight (Carrier) is generally the larger amount
    if (mottruckAmt > 0 && carrierAmt > 0 && mottruckAmt > carrierAmt) {
      const fixed = [...breakdown];
      fixed[mottruckIdx] = {
        ...mottruck,
        amount: carrier.amount,
        vat: carrier.vat,
        total: carrier.total,
      };
      fixed[carrierIdx] = {
        ...carrier,
        amount: mottruck.amount,
        vat: mottruck.vat,
        total: mottruck.total,
      };
      return fixed;
    }
    return breakdown;
  };

  const getPaymentBreakdown = (invoice) =>
    normalizePaymentBreakdown(
      Array.isArray(invoice?.paymentBreakdown) ? invoice.paymentBreakdown : []
    );

  const handleViewInvoice = async (invoice) => {
    setCurrentViewInvoice(invoice);
    setView("view");
    const enriched = await enrichInvoiceQuantityFromShipment(invoice, { silent: true });
    setCurrentViewInvoice(enriched);
  };

  const handleEditInvoice = async (invoice) => {
    setEditingInvoiceId(invoice._id);
    setCurrentViewInvoice(invoice);
    const enriched = await enrichInvoiceQuantityFromShipment(invoice, { silent: true });
    const populated = populateFormFromInvoice(enriched);
    setFormData(populated);
    setView("new");
    if (
      invoice.shipment &&
      isMissingQuantity(populated.operationalDetail?.quantity)
    ) {
      handleShipmentLookup(invoice.shipment);
    }
  };

  const handleDeleteInvoice = async (id) => {
    if (!window.confirm("Are you sure you want to delete this invoice?")) return;
    try {
      const response = await AxiosConfig.instance.post(`/delete_invoice/${id}`);
      if (response.data.success) {
        fetchInvoices();
        if (view !== "list") setView("list");
      }
    } catch (error) {
      console.error("Error deleting invoice:", error);
    }
  };

  const handleNewInvoice = () => {
    setEditingInvoiceId(null);
    setCurrentViewInvoice(null);
    setFormData(resetFormData());
    setView("new");
  };

  const handleSaveInvoice = async () => {
    try {
      const userDetail = JSON.parse(localStorage.getItem("userDetail") || "{}");
      const payload = {
        ...formData,
        date: toDisplayDate(formData.date) || todayDisplayDate(),
        fromDate: toDisplayDate(formData.fromDate),
        toDate: toDisplayDate(formData.toDate),
        invoiceBy: formData.invoiceBy || userDetail?.profile || "Admin",
      };

      let response;
      if (editingInvoiceId) {
        response = await AxiosConfig.instance.post(`/update_invoice/${editingInvoiceId}`, payload);
      } else {
        response = await AxiosConfig.instance.post("/add_invoice", payload);
      }

      if (response.data.success) {
        setView("list");
        setEditingInvoiceId(null);
        fetchInvoices();
        setFormData(resetFormData());
      }
    } catch (error) {
      console.error("Error saving invoice:", error);
    }
  };

  const fetchInvoices = async () => {
    try {
      // Assuming api prefix is configured in AxiosConfig.instance
      const response = await AxiosConfig.instance.get("/get_invoices");
      if (response.data.success) {
        setInvoices(response.data.data);
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  useEffect(() => {
    if (filterOpen) {
      document.body.classList.add("filter_sidebar");
    } else {
      document.body.classList.remove("filter_sidebar");
    }
    return () => document.body.classList.remove("filter_sidebar");
  }, [filterOpen]);

  const toggleBodyClass = () => {
    setIsBodyClassActive(!isBodyClassActive);
  };

  const handleFilterToggle = () => {
    setFilterOpen((prev) => !prev);
  };

  const handleSideFilter = (filterItems) => {
    setActiveFilters(filterItems || {});
  };

  const matchesText = (value, query) => {
    if (!query || !String(query).trim()) return true;
    return String(value || "")
      .toLowerCase()
      .includes(String(query).trim().toLowerCase());
  };

  const matchesDateRange = (rawDate, dateFrom, dateTo) => {
    if (!dateFrom && !dateTo) return true;
    const current = toComparableDate(rawDate);
    if (!current) return false;
    if (dateFrom) {
      const from = toComparableDate(dateFrom);
      if (from && current < from) return false;
    }
    if (dateTo) {
      const to = toComparableDate(dateTo);
      if (to && current > to) return false;
    }
    return true;
  };

  const getInvoiceCustomerLabel = (inv) =>
    inv.customerId || inv.invoiceBy || "—";

  const getInvoiceCarrierLabel = (inv) =>
    inv.carrierId || inv.carrier || "—";

  const getInvoicePartnerColumnLabel = () =>
    isRoleClient ? "CARRIER" : "CUSTOMER";

  const getInvoicePartnerValue = (inv) =>
    isRoleClient ? getInvoiceCarrierLabel(inv) : getInvoiceCustomerLabel(inv);

  const filteredInvoices = invoices.filter((inv) => {
    const searchOk =
      !searchQuery ||
      inv.reference?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.invoiceNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.shipment?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.customerId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.carrierId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.invoiceBy?.toLowerCase().includes(searchQuery.toLowerCase());

    if (!searchOk) return false;

    const f = activeFilters;
    if (!f || Object.keys(f).length === 0) return true;

    if (!matchesDateRange(inv.date, f.dateFrom, f.dateTo)) return false;
    if (!matchesText(inv.status, f.status)) return false;
    if (!matchesText(inv.reference, f.reference) && !matchesText(inv.invoiceNumber, f.reference)) {
      return false;
    }
    if (!matchesText(inv.amount, f.amount)) return false;
    if (!matchesText(inv.shipment, f.shipment)) return false;
    if (isAdminOrStaff && !matchesText(getInvoiceCustomerLabel(inv), f.customer)) {
      return false;
    }
    if (isRoleClient && !matchesText(getInvoiceCarrierLabel(inv), f.customer)) {
      return false;
    }

    return true;
  });

  const selectedInvoicesCount = selectedInvoiceIds.length;
  const allVisibleInvoicesSelected =
    filteredInvoices.length > 0 &&
    filteredInvoices.every((inv) => selectedInvoiceIds.includes(inv._id));

  const toggleExportMode = () => {
    setExportMode((prev) => {
      if (prev) setSelectedInvoiceIds([]);
      return !prev;
    });
  };

  const handleSelectAllInvoices = (checked) => {
    if (checked) {
      setSelectedInvoiceIds(filteredInvoices.map((inv) => inv._id).filter(Boolean));
    } else {
      setSelectedInvoiceIds([]);
    }
  };

  const handleSelectInvoiceRow = (invoiceId) => {
    if (!invoiceId) return;
    setSelectedInvoiceIds((prev) =>
      prev.includes(invoiceId)
        ? prev.filter((id) => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const handleExportInvoicesToExcel = async () => {
    if (selectedInvoicesCount === 0) {
      toast.error("Please select element");
      return;
    }

    const selectedInvoices = filteredInvoices.filter((inv) =>
      selectedInvoiceIds.includes(inv._id)
    );

    // Client/Shipper: Date, Invoice, Status, Amount, Shipment, Carrier
    // Staff: + Customer
    const headers = isAdminOrStaff
      ? ["Date", "Invoice", "Status", "Amount", "Shipment", "Customer"]
      : ["Date", "Invoice", "Status", "Amount", "Shipment", "Carrier"];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Invoices");
    worksheet.addRow(headers);

    selectedInvoices.forEach((inv) => {
      const row = [
        toDisplayDate(inv.date) || inv.date || "",
        inv.invoiceNumber || "",
        inv.status || "",
        inv.amount || "",
        inv.shipment || "",
      ];
      if (isAdminOrStaff) {
        row.push(getInvoiceCustomerLabel(inv));
      } else {
        row.push(getInvoiceCarrierLabel(inv));
      }
      worksheet.addRow(row);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "invoices.xlsx");
    toast.success("Invoices exported successfully.");
    setSelectedInvoiceIds([]);
    setExportMode(false);
  };

  const goBackFromInvoice = () => {
    if (editingInvoiceId && currentViewInvoice) {
      setEditingInvoiceId(null);
      setView("view");
    } else {
      setEditingInvoiceId(null);
      setFormData(resetFormData());
      setView("list");
    }
  };

  const goBackToInvoiceList = () => {
    setEditingInvoiceId(null);
    setCurrentViewInvoice(null);
    setFormData(resetFormData());
    setView("list");
  };

  const getNextInvoiceReferencePreview = () => {
    let maxSeq = 0;
    invoices.forEach((inv) => {
      const match = String(inv.reference || "").match(/^60(\d{4})$/);
      if (match) maxSeq = Math.max(maxSeq, parseInt(match[1], 10));
    });
    return `60${String(maxSeq + 1).padStart(4, "0")}`;
  };

  const renderInvoiceReference = (reference) => {
    if (!reference) return "—";
    return String(reference);
  };

  return (
    <div className="d-flex" id="wrapper">
      <SideBar />
      {filterOpen && (
        <InvoicesFilterSidebar
          close={handleFilterToggle}
          handleFilterFun={handleSideFilter}
          showCustomer={isAdminOrStaff}
          showCarrier={isRoleClient}
        />
      )}
      <div id="page-content-wrapper">
        <div className="topheader">
          <div className="container-fluid">
            <div className="topheader_flex">
              <Button
                variant="primary"
                className="menuBtn"
                onClick={toggleBodyClass}
              >
                <FiMenu />
              </Button>
              <div className="inPutBox">
                {/* Search bar removed from top header to match mockup, or keep if standard */}
              </div>
              <Link className="notiIcon">
                <Image src={NotificationIcon} alt="" />
                <Badge bg="danger">3</Badge>
              </Link>
            </div>
          </div>
        </div>
        
        <div className="invoices-page">
          <Container fluid>
            {view === "list" && (
              <>
                {/* Stats section */}
            <div className="stats-container">
              <div className="stat-card total">
                <h4>Total Invoice</h4>
                <h2>{stats.totalInvoice.toString().padStart(2, '0')}</h2>
              </div>
              <div className="stat-card paid">
                <h4>Paid</h4>
                <h2>{stats.paid.toString().padStart(2, '0')}</h2>
              </div>
              <div className="stat-card pending">
                <h4>Pending</h4>
                <h2>{stats.pending.toString().padStart(2, '0')}</h2>
              </div>
              <div className="stat-card overdue">
                <h4>Overdue</h4>
                <h2>{stats.overdue.toString().padStart(2, '0')}</h2>
              </div>
              <div className="stat-card invoices-count">
                <h4>Invoices</h4>
                <h2>{stats.invoices.toString().padStart(2, '0')}</h2>
              </div>
            </div>

            {/* Table actions section */}
            <div className="table-actions">
              <div className="left-actions">
                {canManageInvoices && (
                  <button className="new-invoice-btn" onClick={handleNewInvoice}>
                    <FiPlus /> New Pre-invoice
                  </button>
                )}
                <div className="search-box">
                  <input
                    type="text"
                    placeholder="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <Image src={SearchIcon} alt="search" />
                </div>
              </div>
              <div className="action-buttons">
                <button
                  type="button"
                  className="toolbar-action-btn"
                  onClick={toggleExportMode}
                >
                  <Image src={ExportIcon} width={18} alt="" /> Export
                </button>
                <button
                  type="button"
                  className="toolbar-action-btn"
                  onClick={handleFilterToggle}
                >
                  <Image src={FilterIcon} width={18} alt="" /> Filters
                </button>
              </div>
            </div>

            {exportMode && (
              <div className="exportStrip">
                <div className="d-flex align-items-center">
                  <IoIosCloseCircleOutline
                    onClick={toggleExportMode}
                    style={{ cursor: "pointer" }}
                  />
                  <p className="ms-2">
                    <strong>{selectedInvoicesCount}</strong> element selected
                  </p>
                </div>
                <button type="button" onClick={handleExportInvoicesToExcel}>
                  Export to Excel
                </button>
              </div>
            )}

            {/* Table section */}
            <div className="invoices-table-container">
              <table>
                <thead>
                  <tr>
                    {exportMode && (
                      <th>
                        <Form.Check
                          type="checkbox"
                          label="All"
                          checked={allVisibleInvoicesSelected}
                          onChange={(e) => handleSelectAllInvoices(e.target.checked)}
                        />
                      </th>
                    )}
                    <th></th>
                    <th>REFERENCE</th>
                    <th>DATE</th>
                    <th>INVOICE</th>
                    <th>STATUS</th>
                    <th>AMOUNT</th>
                    <th>SHIPMENT</th>
                    <th>{getInvoicePartnerColumnLabel()}</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((inv, index) => (
                    <tr key={inv._id || index}>
                      {exportMode && (
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={selectedInvoiceIds.includes(inv._id)}
                            onChange={() => handleSelectInvoiceRow(inv._id)}
                          />
                        </td>
                      )}
                      <td className="row-index">{index + 1}</td>
                      <td>{renderInvoiceReference(inv.reference)}</td>
                      <td>{toDisplayDate(inv.date) || inv.date}</td>
                      <td>{inv.invoiceNumber}</td>
                      <td>
                        <span className={`status-badge ${(inv.status || "").toLowerCase()}`}>
                          {inv.status}
                        </span>
                      </td>
                      <td>{inv.amount}</td>
                      <td className="muted-cell">{inv.shipment}</td>
                      <td>{getInvoicePartnerValue(inv)}</td>
                      <td>
                        <Dropdown className="action-dropdown" align="end">
                          <Dropdown.Toggle
                            variant="link"
                            id={`dropdown-${index}`}
                            className="invoice-row-actions"
                          >
                            <FiMoreVertical size={18} color="#4f5b92" />
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleViewInvoice(inv)}>
                              <FaRegEye /> View
                            </Dropdown.Item>
                            {canManageInvoices && (
                              <>
                                <Dropdown.Item onClick={() => handleEditInvoice(inv)}>
                                  <FaRegEdit /> Modify
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => handleDeleteInvoice(inv._id)} className="delete">
                                  <FaRegTrashAlt /> Delete
                                </Dropdown.Item>
                              </>
                            )}
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))}
                  {filteredInvoices.length === 0 && (
                    <tr>
                      <td colSpan={exportMode ? 10 : 9} className="text-center py-4">No invoices found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            </>
            )}

            {view === "view" && currentViewInvoice && (
              <div className="new-invoice-container">
                <div className="header-info">
                  <h3>
                    Reference Number :{" "}
                    <span>{renderInvoiceReference(currentViewInvoice.reference)}</span>
                  </h3>
                  <h3>
                    Date :{" "}
                    <span>
                      {toDisplayDate(currentViewInvoice.date) || currentViewInvoice.date}
                    </span>
                  </h3>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label>Shipment number</label>
                    <div className="readonly-value">{currentViewInvoice.shipment || "N/A"}</div>
                  </div>
                  <div className="form-group">
                    <label>{partnerIdLabel}</label>
                    <div className="readonly-value">{getPartnerIdValue(currentViewInvoice)}</div>
                  </div>
                  <div className="form-group">
                    <label>Invoice nr.</label>
                    <div className="readonly-value">{currentViewInvoice.invoiceNumber || "N/A"}</div>
                  </div>
                  <div className="form-group">
                    <label>From</label>
                    <div className="readonly-value">
                      {toDisplayDate(currentViewInvoice.fromDate) || currentViewInvoice.fromDate || "N/A"}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Invoice status</label>
                    <div className="readonly-value">{currentViewInvoice.status || "N/A"}</div>
                  </div>
                  <div className="form-group">
                    <label>To</label>
                    <div className="readonly-value">
                      {toDisplayDate(currentViewInvoice.toDate) || currentViewInvoice.toDate || "N/A"}
                    </div>
                  </div>
                </div>

                <div className="section-title">OPERATIONAL DETAIL</div>
                <div className="operational-detail">
                  <div className="detail-box vehicle">
                    <TbTruckDelivery />
                    <div className="info">
                      <span className="label">Vehicle</span>
                      <span className="value">{getOperationalDetail(currentViewInvoice).vehicle}</span>
                    </div>
                  </div>
                  <div className="detail-box quantity">
                    <LuLayers />
                    <div className="info">
                      <span className="label">Quantity</span>
                      <span className="value">{getOperationalDetail(currentViewInvoice).quantity}</span>
                    </div>
                  </div>
                  <div className="detail-box loading">
                    <FiArrowUp />
                    <div className="info">
                      <span className="label">Loading</span>
                      <span className="value" style={{ whiteSpace: "pre-line" }}>
                        {getOperationalDetail(currentViewInvoice).loading}
                      </span>
                    </div>
                  </div>
                  <div className="detail-box unloading">
                    <FiArrowDown />
                    <div className="info">
                      <span className="label">Unloading</span>
                      <span className="value" style={{ whiteSpace: "pre-line" }}>
                        {getOperationalDetail(currentViewInvoice).unloading}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="section-title">PAYMENT BREAKDOWN</div>
                <table className="payment-table">
                  <thead>
                    <tr>
                      <th>PARTY</th>
                      <th>SERVICE</th>
                      <th>AMOUNT</th>
                      <th>VAT</th>
                      <th>TOTAL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getPaymentBreakdown(currentViewInvoice).length > 0 ? (
                      getPaymentBreakdown(currentViewInvoice).map((item, index) => (
                        <tr key={index}>
                          <td>
                            <span className="party-badge">{index + 1}</span> {item.party}
                          </td>
                          <td>{item.service}</td>
                          <td>{item.amount}</td>
                          <td>{item.vat}</td>
                          <td>{item.total}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center py-3">
                          No payment breakdown
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <div className="action-footer">
                  <button type="button" className="delete-btn" onClick={goBackToInvoiceList}>
                    <FiArrowLeft size={16} /> Back
                  </button>
                  {canManageInvoices && (
                    <button
                      className="save-btn"
                      onClick={() => handleEditInvoice(currentViewInvoice)}
                    >
                      Update invoice
                    </button>
                  )}
                </div>
              </div>
            )}

            {view === "new" && canManageInvoices && (
              <div className="new-invoice-container">
                <div className="header-info">
                  <h3>
                    Reference Number :{" "}
                    <span>
                      {editingInvoiceId
                        ? renderInvoiceReference(formData.reference)
                        : renderInvoiceReference(getNextInvoiceReferencePreview())}
                    </span>
                  </h3>
                  <h3>
                    Date :{" "}
                    <span>{toDisplayDate(formData.date) || todayDisplayDate()}</span>
                  </h3>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label>Shipment number</label>
                    <input
                      type="text"
                      name="shipment"
                      placeholder="839209"
                      value={formData.shipment}
                      onChange={handleInputChange}
                      onBlur={() => handleShipmentLookup()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleShipmentLookup();
                        }
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Customer ID</label>
                    <input type="text" name="customerId" placeholder="Enter name or account" value={formData.customerId} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label>Invoice nr.</label>
                    <input
                      type="text"
                      name="invoiceNumber"
                      placeholder="Optional"
                      value={formData.invoiceNumber}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>From</label>
                    <input type="date" name="fromDate" value={formData.fromDate} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label>Invoice status</label>
                    <select name="status" value={formData.status} onChange={handleInputChange}>
                      <option value="Pending">Pending</option>
                      <option value="Paid">Paid</option>
                      <option value="Overdue">Overdue</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>To</label>
                    <input type="date" name="toDate" value={formData.toDate} onChange={handleInputChange} />
                  </div>
                </div>

                <div className="section-title">OPERATIONAL DETAIL</div>
                <div className="operational-detail">
                  <div className="detail-box vehicle">
                    <TbTruckDelivery />
                    <div className="info">
                      <span className="label">Vehicle</span>
                      <span className="value">{formData.operationalDetail.vehicle}</span>
                    </div>
                  </div>
                  <div className="detail-box quantity">
                    <LuLayers />
                    <div className="info">
                      <span className="label">Quantity</span>
                      <span className="value">{formData.operationalDetail.quantity}</span>
                    </div>
                  </div>
                  <div className="detail-box loading">
                    <FiArrowUp />
                    <div className="info">
                      <span className="label">Loading</span>
                      <span className="value" style={{ whiteSpace: 'pre-line' }}>{formData.operationalDetail.loading}</span>
                    </div>
                  </div>
                  <div className="detail-box unloading">
                    <FiArrowDown />
                    <div className="info">
                      <span className="label">Unloading</span>
                      <span className="value" style={{ whiteSpace: 'pre-line' }}>{formData.operationalDetail.unloading}</span>
                    </div>
                  </div>
                </div>

                <div className="section-title">PAYMENT BREAKDOWN</div>
                <table className="payment-table">
                  <thead>
                    <tr>
                      <th>PARTY</th>
                      <th>SERVICE</th>
                      <th>AMOUNT</th>
                      <th>VAT</th>
                      <th>TOTAL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.paymentBreakdown.map((item, index) => (
                      <tr key={index}>
                        <td><span className="party-badge">{index + 1}</span> {item.party}</td>
                        <td>{item.service}</td>
                        <td>{item.amount}</td>
                        <td>{item.vat}</td>
                        <td>{item.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="action-footer">
                  <button type="button" className="delete-btn" onClick={goBackFromInvoice}>
                    <FiArrowLeft size={16} /> Back
                  </button>
                  <button className="save-btn" onClick={handleSaveInvoice}>
                    {editingInvoiceId ? "Update invoice" : "Save information"}
                  </button>
                </div>
              </div>
            )}
          </Container>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={4000} />
    </div>
  );
};

export default Invoices;
